import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  getAllSuggestions,
  updateSuggestionStatus,
  addSuggestionResponse,
  type SuggestionReview,
} from "@/services/suggestionsService";
import {
  Star,
  CheckCircle,
  XCircle,
  Loader2,
  MessageSquare,
  Send,
} from "lucide-react";
import { toast } from "sonner";

const SUGGESTIONS_QUERY_KEY = ["suggestions-all"] as const;

function formatDate(createdAt: SuggestionReview["createdAt"]): string {
  if (!createdAt) return "\u2014";
  const date = "seconds" in createdAt ? new Date(createdAt.seconds * 1000) : new Date(createdAt);
  return date.toLocaleDateString("es-DO", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

type StatusTab = "todas" | "pendiente" | "aprobado" | "rechazado";

const STATUS_TABS: { value: StatusTab; label: string }[] = [
  { value: "todas", label: "Todas" },
  { value: "pendiente", label: "Pendientes" },
  { value: "aprobado", label: "Aprobadas" },
  { value: "rechazado", label: "Rechazadas" },
];

export function Suggestions() {
  const queryClient = useQueryClient();
  const { data: list = [], isLoading: loading } = useQuery({
    queryKey: SUGGESTIONS_QUERY_KEY,
    queryFn: getAllSuggestions,
  });

  const [statusFilter, setStatusFilter] = useState<StatusTab>("todas");
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [responseTexts, setResponseTexts] = useState<Record<string, string>>({});
  const [expandedResponse, setExpandedResponse] = useState<string | null>(null);

  const filtered = statusFilter === "todas"
    ? list
    : list.filter((s) => s.status === statusFilter);

  const handleStatusChange = async (id: string, status: string) => {
    setActionLoading(`${id}-${status}`);
    try {
      await updateSuggestionStatus(id, status);
      await queryClient.invalidateQueries({ queryKey: SUGGESTIONS_QUERY_KEY });
      toast.success(
        status === "aprobado" ? "Sugerencia aprobada" : "Sugerencia rechazada"
      );
    } catch {
      toast.error("Error al cambiar el estado");
    } finally {
      setActionLoading(null);
    }
  };

  const handleResponse = async (id: string) => {
    const text = (responseTexts[id] ?? "").trim();
    if (!text) {
      toast.error("Escribe una respuesta");
      return;
    }
    setActionLoading(`${id}-response`);
    try {
      await addSuggestionResponse(id, text);
      await queryClient.invalidateQueries({ queryKey: SUGGESTIONS_QUERY_KEY });
      setResponseTexts((prev) => ({ ...prev, [id]: "" }));
      setExpandedResponse(null);
      toast.success("Respuesta enviada");
    } catch {
      toast.error("Error al enviar respuesta");
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-semibold">Buz&oacute;n de sugerencias</h1>
        <p className="text-muted-foreground">
          Sugerencias y rese&ntilde;as enviadas desde la app por los usuarios
        </p>
      </div>

      {/* Filter tabs */}
      <Tabs value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusTab)}>
        <TabsList className="flex flex-wrap gap-1">
          {STATUS_TABS.map((tab) => (
            <TabsTrigger key={tab.value} value={tab.value}>
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            {list.length === 0
              ? "No hay sugerencias a\u00fan"
              : "No hay sugerencias con este filtro"}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filtered.map((s) => (
            <Card key={s.id} id={s.id}>
              <CardHeader className="pb-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
                      <Star className="h-3.5 w-3 fill-current" />
                      <span className="text-sm font-medium">{s.rating}</span>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {s.userName || s.userEmail || "An\u00f3nimo"}
                    </span>
                    {s.agencyName && (
                      <span className="text-sm text-muted-foreground">&middot; {s.agencyName}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={s.status === "aprobado" ? "default" : s.status === "rechazado" ? "destructive" : "secondary"}>
                      {s.status}
                    </Badge>
                    <span className="text-xs text-muted-foreground">{formatDate(s.createdAt)}</span>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="whitespace-pre-wrap text-sm">{s.description || "\u2014"}</p>

                {/* Existing response */}
                {(s as SuggestionReview & { response?: string }).response && (
                  <div className="rounded-lg border bg-muted/50 p-3 text-sm">
                    <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
                      <MessageSquare className="h-3.5 w-3.5" />
                      <span className="font-medium">Respuesta del administrador</span>
                    </div>
                    <p className="whitespace-pre-wrap">
                      {(s as SuggestionReview & { response?: string }).response}
                    </p>
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={actionLoading !== null || s.status === "aprobado"}
                    onClick={() => handleStatusChange(s.id, "aprobado")}
                    className="gap-2 text-green-700 border-green-300 hover:bg-green-50"
                  >
                    {actionLoading === `${s.id}-aprobado` ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <CheckCircle className="h-4 w-4" />
                    )}
                    Aprobar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={actionLoading !== null || s.status === "rechazado"}
                    onClick={() => handleStatusChange(s.id, "rechazado")}
                    className="gap-2 text-destructive border-destructive/30 hover:bg-destructive/5"
                  >
                    {actionLoading === `${s.id}-rechazado` ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <XCircle className="h-4 w-4" />
                    )}
                    Rechazar
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setExpandedResponse(expandedResponse === s.id ? null : s.id)
                    }
                    className="gap-2"
                  >
                    <MessageSquare className="h-4 w-4" />
                    Responder
                  </Button>
                </div>

                {/* Response textarea */}
                {expandedResponse === s.id && (
                  <div className="flex gap-2 pt-1">
                    <Textarea
                      value={responseTexts[s.id] ?? ""}
                      onChange={(e) =>
                        setResponseTexts((prev) => ({ ...prev, [s.id]: e.target.value }))
                      }
                      placeholder="Escribe tu respuesta..."
                      rows={2}
                      className="flex-1"
                    />
                    <Button
                      size="sm"
                      disabled={actionLoading === `${s.id}-response` || !(responseTexts[s.id] ?? "").trim()}
                      onClick={() => handleResponse(s.id)}
                      className="gap-2 self-end"
                    >
                      {actionLoading === `${s.id}-response` ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                      Enviar
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
